/**
 * 作业录入表单的**科目候选**（"科目先定义好，下拉选，不用每次手输"）。
 *
 * ## 候选从哪来（**没有新增表、没有迁移、没有新增接口**）
 *
 * 只用两份**已有的**只读数据，按"最近用过"排序：
 *
 *   1. **这一天已有的科目**（`GET /students/:id/homework` 的 `subjects`，
 *      含 `retained` 的保留科目）—— 它们是"此刻正在用"的科目，
 *      必须排在最前面，否则家长改一科时还得在长下拉里找；
 *   2. **过往作业单里录过的科目**（`GET /students/:id/homework/history` 的
 *      `sheets[].subjects`，服务端按日期倒序返回 → 天然是"最近的在前"）。
 *
 * 去重与限长走 `@fht/shared` 的 `collectHomeworkSubjectNames()`：纯函数、
 * 与页面共用同一份实现（排序口径不会出现"测试和线上各一套"）。
 *
 * ## 为什么不是一张 `subject_preset` 表
 *
 * 加表的代价是**迁移 + 一套增删改接口 + 一组权限 + 一份新 UI**（"管理我的科目预设"），
 * 而收益只是"候选里少几个不想要的词"。而"我以前录过的科目"本来就是家长心里
 * "我定义过的科目"的实际含义，历史数据里已经有了 —— 于是这里的取舍是：
 * **先从历史里收集，等真的出现"候选太脏"的抱怨再考虑预设表**。
 *
 * ## 与历史区的关系
 *
 * 这一页的「查过往的作业」区块用的是**同一个** `useHomeworkHistory` 与同一个
 * queryKey（区间也一致：最近 30 天），所以两边不会各打一次接口。
 */

import {
  collectHomeworkSubjectNames,
  normalizeHomeworkSubjectName,
  type HomeworkDraftSubject,
} from '@fht/shared';
import type { HomeworkHistoryResult } from './api/homework';

/** 从一天里取科目名（保留项也照收：那些名字家长以前真的用过）。 */
export function subjectNamesOfDraftDay(drafts: readonly HomeworkDraftSubject[]): string[] {
  return drafts.map((subject) => subject.name);
}

/** 从一天的服务端视图里取科目名（含 `retained` 的整科）。 */
export function subjectNamesOfDaySubjects(
  subjects: readonly { name: string }[] | undefined,
): string[] {
  return (subjects ?? []).map((subject) => subject.name);
}

/**
 * 科目的下拉候选（越靠前越"最近用过"）。
 *
 * 顺序：**今天这一天的科目** → 历史作业单（服务端按日期倒序）里的科目。
 */
export function subjectSuggestionsOf(
  daySubjects: readonly { name: string }[] | undefined,
  history: HomeworkHistoryResult | undefined,
): string[] {
  return collectHomeworkSubjectNames([
    { names: subjectNamesOfDaySubjects(daySubjects) },
    ...(history?.sheets ?? []).map((sheet) => ({ names: sheet.subjects })),
  ]);
}

/**
 * 下拉里"输入新的科目…"那个选项的值。
 *
 * ⚠️ 用一个**不可能与真实科目名相同**的哨兵（`:new:`）而不是"空字符串"：
 * 空串是"还没填"的合法状态，而"新科目"是一个**明确的动作**。
 * 名字里带冒号在真实科目录入里虽然不违规，但这里只影响"选了它之后显示输入框"，
 * 不改变任何提交语义 —— 万一真有家长把科目叫 `:new:`，它只是多显示一个输入框。
 */
export const HOMEWORK_SUBJECT_NEW_OPTION = ':new:';

/** 下拉里"还没选"的占位项（`value=''`）。它**不可选**，只是把"没选"说出来。 */
export const HOMEWORK_SUBJECT_PLACEHOLDER = '选择科目…';

/** 科目那一格此时此刻该长什么样（下拉 / 输入框 + 下拉的选项与选中值）。 */
export interface HomeworkSubjectFieldState {
  /** `select` = 原生下拉（手机上是原生选择器）；`input` = 自由输入框。 */
  mode: 'select' | 'input';
  /**
   * 下拉里的选项。
   *
   * ⚠️ 它可能比 `suggestions` **多一项**：当前名字不在候选里时（服务端来的自定义名，
   * 或家长刚手输的名字）会把**当前名字补在最前面**。
   * 不补的话 `<select>` 的 value 找不到对应 option，浏览器就会显示成
   * "**没选中任何项**"（用户上一轮反馈的正是这种观感）。
   */
  options: string[];
  /**
   * 传给 `<select value>` 的值。不变式：**要么是 `''`（显示占位项），
   * 要么一定能在 `options` 里找到** —— 永远不出现"看着没选中"的下拉。
   */
  value: string;
}

/**
 * 科目那一格的状态（**纯函数**，2026-10-14 那个"下拉没生效"的 bug 就修在这里）。
 *
 * ## 那个 bug 是什么
 *
 * 旧实现把"显示下拉还是输入框"存成一次性的 `useState` 初值：
 *
 * ```ts
 * useState(() => name === '' || !suggestions.includes(name))
 * ```
 *
 * 于是**新增科目行（名字是空的）永远落到"输入框"那一支** —— 空名字当然不在候选里。
 * 而"新增一科"恰恰是这个页面的主路径（今天还没有作业时，表单里唯一的那一行就是
 * 空名字的新行），结果就是用户看到的：**下拉形同虚设，每次还得手输**。
 * 而且这个初值只算一次：候选（历史科目）是异步回来的，回来之后也不会再切回下拉。
 *
 * ## 现在的口径（三条，缺一不可）
 *
 * 1. **没有名字 = 显示下拉**（而不是显示输入框）：新行一出现就能看见候选，
 *    用户那句话（"能不能先定义好、然后下拉选"）才算真的兑现；
 * 2. **名字在候选里 = 显示下拉，且 value 就是那个名字**（选中的项一定看得见）；
 * 3. **名字不在候选里 = 仍显示下拉，并把当前名字补成一个选项**：
 *    家长改得动它（选「＋ 输入新的科目…」就进输入框），
 *    下拉也**不会**看起来"什么都没选中"。
 *
 * 唯一需要**用户显式动作**才能进的形态是输入框（`newSubjectMode`）：
 * 只有点了「＋ 输入新的科目…」才进去，而且页面上给了回下拉的出口
 * （不能把"新增科目"这条路堵死，也不能让家长进去出不来）。
 */
export function homeworkSubjectFieldState(input: {
  /** 草稿里这一科当前的名字（可能是空串）。 */
  name: string;
  /** 这一科的下拉候选（`subjectSuggestionsOf()` 的结果）。 */
  suggestions: readonly string[];
  /** 用户是否已经点了「＋ 输入新的科目…」。 */
  newSubjectMode: boolean;
}): HomeworkSubjectFieldState {
  const raw = input.name;
  const trimmed = normalizeHomeworkSubjectName(raw);
  const suggestions = [...input.suggestions];

  // 名字与候选的对应关系：先按原样比（能对上就用候选里那一份），再按"去掉首尾空白"比。
  const known =
    trimmed === ''
      ? undefined
      : (suggestions.find((name) => name === raw) ?? suggestions.find((name) => name === trimmed));

  const options = known !== undefined || trimmed === '' ? suggestions : [raw, ...suggestions];
  const value = known ?? (trimmed === '' ? '' : raw);

  return { mode: input.newSubjectMode ? 'input' : 'select', options, value };
}

